import { PlayerPageFilters, ResetAllFiltersButton } from '@statsbomb/kitbag-global-football';
import { useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { Suspense } from 'react';
import { useMatch } from 'react-router-dom';
import { competitionOptionsAtom } from '@/atoms/filters/highLevel/competitions';
import { positionOptionsAtom } from '@/atoms/filters/highLevel/positions';
import { seasonOptionsAtom } from '@/atoms/filters/highLevel/seasons';
import { teamOptionsAtom } from '@/atoms/filters/highLevel/teams';
import { playerGamesBySeasonAtom } from '@/atoms/filters/player/playerGames';
import { playerIdAtom } from '@/atoms/player/player';
import { unwrappedAvailableEventPeriodsAtom } from '@/atoms/queries/availableEventPeriods';
import { playerCyclesOrderedAtom, unwrappedSquadPlayerOptionsAtom } from '@/atoms/queries/players';
import { AIFilterPromptPanel } from '@/components/ai/AIFilterPromptPanel';
import { usePhysicalMetricsFeature } from '@/hooks/featureFlags';
import { useAiFilterPromptFeatureToggle } from '@/hooks/featureFlags/useAiFilterPromptFeatureToggle';
import { useComparisonFilterTabsActive } from '@/hooks/useComparisonFilterTabsActive';
import { toGameSelectorGroups } from '@/utils/games';

const unwrappedPlayerCyclesOrderedAtom = unwrap(playerCyclesOrderedAtom);
// Unwrap the option atoms so that recomputing them (e.g. season/team options
// re-derive when the competition changes) keeps the previously-resolved value
// instead of suspending. Without this the whole filter panel briefly unmounts
// to the Suspense fallback while the new options resolve.
const unwrappedCompetitionOptionsAtom = unwrap(competitionOptionsAtom, prev => prev ?? []);
const unwrappedSeasonOptionsAtom = unwrap(seasonOptionsAtom, prev => prev ?? []);
const unwrappedTeamOptionsAtom = unwrap(teamOptionsAtom, prev => prev ?? []);
const unwrappedPositionOptionsAtom = unwrap(positionOptionsAtom, prev => prev ?? []);
const unwrappedPlayerGamesBySeasonAtom = unwrap(playerGamesBySeasonAtom, prev => prev ?? []);

const PlayerHighLevelFiltersWithData = () => {
  const competitionOptions = useAtomValue(unwrappedCompetitionOptionsAtom);
  const seasonOptions = useAtomValue(unwrappedSeasonOptionsAtom);
  const teamOptions = useAtomValue(unwrappedTeamOptionsAtom);
  const positionOptions = useAtomValue(unwrappedPositionOptionsAtom);
  const playerCycles = useAtomValue(unwrappedPlayerCyclesOrderedAtom);
  const availableMatchPeriods = useAtomValue(unwrappedAvailableEventPeriodsAtom);
  const squadPlayerOptions = useAtomValue(unwrappedSquadPlayerOptionsAtom);
  const isPlayerVisPage = !!useMatch('/:locale/player/:playerId/vis/*');
  const isPlayerMatchesPage = !!useMatch('/:locale/player/:playerId/matches/*');
  const showTracking = usePhysicalMetricsFeature();
  const aiFilterPromptEnabled = useAiFilterPromptFeatureToggle();
  const showInlineReset = useComparisonFilterTabsActive();
  const playerGamesBySeason = useAtomValue(unwrappedPlayerGamesBySeasonAtom);
  const gamesGroupedBySeason = toGameSelectorGroups(playerGamesBySeason);

  const playerId = useAtomValue(playerIdAtom);

  if (!playerId) return null;

  return (
    <PlayerPageFilters
      playerId={playerId}
      competitionOptions={competitionOptions}
      seasonOptions={seasonOptions}
      teamOptions={teamOptions}
      positionOptions={positionOptions}
      playerCycles={playerCycles}
      gamesGroupedBySeason={gamesGroupedBySeason}
      showTabs={isPlayerVisPage}
      hidePosition={isPlayerMatchesPage}
      hideAttributes={!isPlayerVisPage}
      hidePitch={!isPlayerVisPage}
      availableMatchPeriods={isPlayerVisPage ? availableMatchPeriods : undefined}
      squadPlayerOptions={isPlayerVisPage ? squadPlayerOptions : undefined}
      aiFilterPromptPanel={isPlayerVisPage && aiFilterPromptEnabled ? <AIFilterPromptPanel /> : undefined}
      headerActions={showInlineReset ? <ResetAllFiltersButton /> : undefined}
      showTracking={showTracking}
    />
  );
};

export const PlayerHighLevelFilters = () => (
  <Suspense fallback={null}>
    <PlayerHighLevelFiltersWithData />
  </Suspense>
);
